import styled, { css, keyframes } from 'styled-components';
import { SIDEBAR_MOBILE_BREAKPOINT } from '@constants/sidebar';

const enterFromTop = keyframes`
  from { opacity: 0; transform: translate(-50%, -6px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

const enterFromBottom = keyframes`
  from { opacity: 0; transform: translate(-50%, 6px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

/**
 * Outer absolute-positioned wrapper that pins the floating toolbar over
 * the canvas. The actual chrome (rounded surface, elevation) comes from
 * `@aibuild/ui` `Toolbar variant="floating"` -- the wrapper just owns
 * positioning + the slide-in animation.
 *
 * `pointer-events` are scoped: the wrapper itself catches no pointer
 * events (so the user can still pan / box-select on the canvas under
 * it), but the toolbar itself re-enables them so its buttons remain
 * interactive.
 */
export const ToolbarWrapper = styled.div<{ $position: 'top' | 'bottom' }>`
  position: absolute;
  left: 50%;
  z-index: 12;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }

  ${({ $position }) =>
    $position === 'top'
      ? css`
          top: 8px;
          animation: ${enterFromTop} 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
        `
      : css`
          bottom: 16px;
          animation: ${enterFromBottom} 220ms cubic-bezier(0.2, 0.8, 0.2, 1)
            both;
        `}

  @media (max-width: ${SIDEBAR_MOBILE_BREAKPOINT}px) {
    max-width: calc(100% - 16px);
    overflow-x: auto;
    scrollbar-width: none;
  }
`;

/**
 * Mode-badge cluster: the badge itself plus the published-only inline
 * action buttons live in one inline-flex row so they read as a single
 * group at the left of the top toolbar. Uses a wider 4px gap than the
 * surrounding toolbar's compact 2px so the badge+actions read as one
 * tight unit, not as part of the wider toolbar stride.
 */
export const ModeCluster = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

/**
 * Inline label inside the published "Runs" Button. The handoff
 * (.ctb__btn-text) uses a slightly smaller weight-500 label with a
 * little extra letter-spacing -- aligns visually with the surrounding
 * 22px material symbols rather than the Button's default 12.5px label.
 */
export const RunsLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  white-space: nowrap;
`;
